import React, { useEffect, useRef, useState } from 'react';
import { Copy, Undo2, X } from 'lucide-react';
import { buildAssistBody } from '../../utils/boothAssist';
import {
  BoothClientError,
  boothFetch,
  explainError,
  shouldRetryPass,
} from '../../utils/boothClient';
import { lineEndingWord, swapLastWord } from '../../utils/reflectEditor';

export { buildAssistBody } from '../../utils/boothAssist';

const AI_OK_KEY = 'sp_booth_ai_ok';
const PASS_KEY = 'sp_booth_pass';
const TURNSTILE_SCRIPT_ID = 'sp-booth-turnstile';

let turnstileScriptPromise = null;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Quick check did not load.'));
    };
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });
  return turnstileScriptPromise;
};

const readFlag = () => {
  try {
    return localStorage.getItem(AI_OK_KEY) === '1';
  } catch (_error) {
    return false;
  }
};

const readPass = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PASS_KEY) || 'null');
    return parsed?.token ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const writePass = (value) => {
  try {
    if (value) sessionStorage.setItem(PASS_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PASS_KEY);
  } catch (_error) {
    // A pass can still be used in component memory for this visit.
  }
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_error) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    area.remove();
    return copied;
  }
};

const PizzulMoves = ({ color, config, dispatch, focusedLine, onCare, sheet }) => {
  const [disclosed, setDisclosed] = useState(readFlag);
  const [disclosureMove, setDisclosureMove] = useState(null);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [challengeError, setChallengeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refusal, setRefusal] = useState(null);
  const [result, setResult] = useState(null);
  const [rhymeOpen, setRhymeOpen] = useState(false);
  const [targetWord, setTargetWord] = useState('');
  const [meaning, setMeaning] = useState('');
  const [leftToday, setLeftToday] = useState(config?.moves_left_today);
  const [undo, setUndo] = useState(null);
  const [copied, setCopied] = useState('');
  const passRef = useRef(readPass());
  const challengeRef = useRef(null);
  const challengePromiseRef = useRef(null);
  const undoTimer = useRef(null);
  const copyTimer = useRef(null);

  const section = focusedLine ? sheet.sections?.[focusedLine.section] : null;
  const focusedText = focusedLine ? section?.lines?.[focusedLine.line]?.text || '' : '';
  const available = Boolean(config?.ai_available && config?.turnstile_site_key && !refusal);
  const disabledCaption = !available
    ? (refusal
      ? explainError(refusal.status, refusal.detail)
      : explainError(503, config?.ai_reason || 'resting'))
    : '';

  useEffect(() => {
    setLeftToday(config?.moves_left_today);
  }, [config?.moves_left_today]);

  useEffect(() => () => {
    clearTimeout(undoTimer.current);
    clearTimeout(copyTimer.current);
    challengePromiseRef.current?.reject(new Error('Quick check closed.'));
  }, []);

  useEffect(() => {
    if (!challengeOpen || !challengeRef.current) return undefined;
    let widgetId;
    let live = true;
    loadTurnstile()
      .then((turnstile) => {
        if (!live || !challengeRef.current) return;
        widgetId = turnstile.render(challengeRef.current, {
          sitekey: config.turnstile_site_key,
          action: 'booth',
          appearance: 'interaction-only',
          callback: (token) => {
            challengePromiseRef.current?.resolve(token);
            challengePromiseRef.current = null;
            setChallengeOpen(false);
          },
          'error-callback': () => {
            challengePromiseRef.current?.reject(new Error('Quick check failed. Try again.'));
            challengePromiseRef.current = null;
            setChallengeError('Quick check failed. Try again.');
          },
          'expired-callback': () => {
            challengePromiseRef.current?.reject(new Error('Quick check expired. Try again.'));
            challengePromiseRef.current = null;
            setChallengeError('Quick check expired. Try again.');
          },
        });
      })
      .catch(() => {
        challengePromiseRef.current?.reject(new Error('Quick check did not load. Try again.'));
        challengePromiseRef.current = null;
        setChallengeError('Quick check did not load. Try again.');
      });
    return () => {
      live = false;
      if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [challengeOpen, config?.turnstile_site_key]);

  const requestChallenge = () => new Promise((resolve, reject) => {
    challengePromiseRef.current = { resolve, reject };
    setChallengeError('');
    setChallengeOpen(true);
  });

  const getPass = async () => {
    const turnstileToken = await requestChallenge();
    const response = await boothFetch('pass', {
      method: 'POST',
      body: { turnstile_token: turnstileToken },
    });
    const next = { token: response.pass, moves_left: response.moves };
    passRef.current = next;
    writePass(next);
    return next;
  };

  const sendAssist = async (body, retried = false) => {
    const currentPass = passRef.current?.token ? passRef.current : await getPass();
    try {
      const response = await boothFetch('assist', {
        method: 'POST',
        body,
        pass: currentPass.token,
      });
      const movesLeft = Math.max(0, Number(currentPass.moves_left ?? config.pass_moves) - 1);
      passRef.current = { ...currentPass, moves_left: movesLeft };
      writePass(passRef.current);
      return response;
    } catch (requestError) {
      if (shouldRetryPass(requestError.status, requestError.detail, retried)) {
        passRef.current = null;
        writePass(null);
        await getPass();
        return sendAssist(body, true);
      }
      throw requestError;
    }
  };

  const runMove = async (move, extra = {}) => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const body = buildAssistBody(sheet, focusedLine, move, extra);
      const response = await sendAssist(body);
      if (response.care) {
        onCare(response.care);
        return;
      }
      setResult({ ...response, move });
      if (Number.isFinite(Number(response.left_today))) {
        setLeftToday(Number(response.left_today));
      }
    } catch (requestError) {
      if (requestError instanceof BoothClientError && (
        requestError.status === 503
        || (requestError.status === 429 && requestError.detail === 'daily_limit')
      )) {
        setRefusal({ status: requestError.status, detail: requestError.detail });
      }
      setError(requestError instanceof BoothClientError
        ? requestError.message : 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const beginMove = (move, extra = {}) => {
    if (!available || loading) return;
    if (!disclosed) {
      setDisclosureMove({ move, extra });
      return;
    }
    runMove(move, extra);
  };

  const acceptDisclosure = () => {
    try {
      localStorage.setItem(AI_OK_KEY, '1');
    } catch (_error) {
      // The in-memory flag avoids repeating the card during this visit.
    }
    setDisclosed(true);
    const pending = disclosureMove;
    setDisclosureMove(null);
    if (pending?.move === 'open_rhymes') {
      setTargetWord(lineEndingWord(focusedText));
      setMeaning('');
      setRhymeOpen(true);
    } else if (pending) {
      runMove(pending.move, pending.extra);
    }
  };

  const openRhymes = () => {
    if (!disclosed) {
      setDisclosureMove({ move: 'open_rhymes', extra: {} });
      return;
    }
    setTargetWord(lineEndingWord(focusedText));
    setMeaning('');
    setRhymeOpen(true);
  };

  const markCopied = (value) => {
    setCopied(value);
    clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(''), 1400);
  };

  const copyOption = async (option) => {
    if (await copyText(option)) markCopied(option);
  };

  const applyOption = async (option) => {
    if (result.move === 'finish_line' || result.move === 'punch_up') {
      if (!focusedLine) return;
      const previous = sheet.sections?.[focusedLine.section]?.lines?.[focusedLine.line]?.text || '';
      dispatch({
        type: 'edit_line',
        section: focusedLine.section,
        line: focusedLine.line,
        text: option,
      });
      setUndo({ ...focusedLine, text: previous });
      clearTimeout(undoTimer.current);
      undoTimer.current = window.setTimeout(() => setUndo(null), 6000);
      return;
    }
    if (result.move === 'rhymes_that_mean' && focusedLine) {
      const current = sheet.sections?.[focusedLine.section]?.lines?.[focusedLine.line]?.text || '';
      dispatch({
        type: 'edit_line',
        section: focusedLine.section,
        line: focusedLine.line,
        text: swapLastWord(current, option),
      });
      return;
    }
    await copyOption(option);
  };

  const restoreOption = () => {
    if (!undo) return;
    dispatch({
      type: 'edit_line',
      section: undo.section,
      line: undo.line,
      text: undo.text,
    });
    setUndo(null);
    clearTimeout(undoTimer.current);
  };

  const blocked = loading || !available;
  const pose = loading ? 'thinking' : result?.verdict === 'ok' ? 'satisfied' : 'confused';

  return (
    <div className="pizzul-moves booth-editor-control mt-3 rounded-2xl border border-white/10
      bg-[#0b0d0e]/95 p-3 shadow-xl backdrop-blur-xl"
      style={{ '--glow': color }}
    >
      <div className="flex items-start gap-3">
        <img
          src={`/pizzul/${pose}.png`}
          alt="Pizzul"
          className={`h-12 w-12 shrink-0 object-contain ${loading ? 'animate-pulse' : ''}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-heading text-lg font-black" style={{ color }}>Pizzul moves</p>
            {Number.isFinite(Number(leftToday)) && (
              <p className="text-sm font-bold text-white/45">{leftToday} left today</p>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={blocked || !focusedLine}
              onClick={() => beginMove('finish_line')}
              className="min-h-11 rounded-full border border-white/15 px-3 text-sm font-black
                hover:border-[color:var(--glow)] disabled:opacity-35"
            >
              Finish this line
            </button>
            <button
              type="button"
              disabled={blocked || !focusedLine || !focusedText.trim()}
              onClick={() => beginMove('punch_up')}
              className="min-h-11 rounded-full border border-white/15 px-3 text-sm font-black
                hover:border-[color:var(--glow)] disabled:opacity-35"
            >
              Punch it up
            </button>
            <button
              type="button"
              disabled={blocked}
              onClick={openRhymes}
              className="min-h-11 rounded-full border border-white/15 px-3 text-sm font-black
                hover:border-[color:var(--glow)] disabled:opacity-35"
            >
              Rhymes that mean
            </button>
            <button
              type="button"
              disabled={blocked}
              onClick={() => beginMove('stuck')}
              className="min-h-11 rounded-full border border-white/15 px-3 text-sm font-black
                hover:border-[color:var(--glow)] disabled:opacity-35"
            >
              I'm stuck
            </button>
          </div>
          {disabledCaption && <p className="mt-2 text-sm font-bold text-white/50">{disabledCaption}</p>}
          {error && <p role="alert" className="mt-2 text-base font-bold text-[#FFB19C]">{error}</p>}
        </div>
      </div>

      {rhymeOpen && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!targetWord.trim()) return;
            setRhymeOpen(false);
            beginMove('rhymes_that_mean', {
              target_word: targetWord,
              meaning,
            });
          }}
          className="mt-3 grid gap-2 rounded-xl border border-white/10 bg-black/20 p-3 sm:grid-cols-2"
        >
          <label className="text-sm font-black text-white/60">
            Rhyme with
            <input
              value={targetWord}
              onChange={(event) => setTargetWord(event.target.value)}
              maxLength={40}
              className="mt-1 min-h-11 w-full rounded-xl border border-white/15 bg-black/30
                px-3 text-base text-white outline-none focus:border-[color:var(--glow)]"
            />
          </label>
          <label className="text-sm font-black text-white/60">
            About
            <input
              value={meaning}
              onChange={(event) => setMeaning(event.target.value)}
              maxLength={80}
              className="mt-1 min-h-11 w-full rounded-xl border border-white/15 bg-black/30
                px-3 text-base text-white outline-none focus:border-[color:var(--glow)]"
            />
          </label>
          <div className="flex gap-2 sm:col-span-2 sm:justify-end">
            <button
              type="button"
              onClick={() => setRhymeOpen(false)}
              className="min-h-11 rounded-full px-4 text-sm font-black text-white/55"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!targetWord.trim() || loading}
              className="neon-btn min-h-11 rounded-full px-5 text-sm font-black text-black
                disabled:opacity-35"
              style={{ backgroundColor: color }}
            >
              Ask Pizzul
            </button>
          </div>
        </form>
      )}

      {result && (
        <div className="mt-3 border-t border-white/10 pt-3">
          {result.note && (
            <p className="rounded-2xl rounded-tl-sm border border-white/10 bg-white/[0.06]
              p-3 text-base font-bold leading-relaxed text-white/80"
            >
              {result.note}
            </p>
          )}
          {result.verdict === 'ok' && result.options?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {result.options.map((option) => (
                <div key={option} className="flex min-h-11 overflow-hidden rounded-full border
                  border-[color:var(--glow)] bg-white/[0.04]"
                >
                  <button
                    type="button"
                    onClick={() => applyOption(option)}
                    className="min-h-11 px-3 text-left text-base font-black"
                  >
                    {option}
                  </button>
                  <button
                    type="button"
                    onClick={() => copyOption(option)}
                    className="flex min-h-11 min-w-11 items-center justify-center border-l
                      border-white/10 text-white/55 hover:text-white"
                    aria-label={`Copy ${option}`}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {copied && <p className="mt-2 text-sm font-black" style={{ color }}>Copied</p>}
        </div>
      )}

      {disclosureMove && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4"
          role="dialog" aria-modal="true" aria-labelledby="booth-disclosure-title"
        >
          <div className="w-full max-w-xl rounded-[1.75rem] border border-white/15 bg-[#111415] p-6">
            <h2 id="booth-disclosure-title" className="font-heading text-3xl font-black">
              Before Pizzul jumps in
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/70">
              Pizzul's AI sends only the line you're on and a few lines above it to Anthropic,
              the company behind Claude, to come up with ideas. We don't save your lyrics.
              Keep real names, addresses and numbers out of it.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setDisclosureMove(null)}
                className="min-h-12 rounded-full px-5 text-base font-black text-white/60"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={acceptDisclosure}
                className="neon-btn min-h-12 rounded-full px-6 text-base font-black text-black"
                style={{ backgroundColor: color }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {challengeOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-4"
          role="dialog" aria-modal="true" aria-labelledby="booth-challenge-title"
        >
          <div className="w-full max-w-sm rounded-[1.75rem] border border-white/15 bg-[#111415] p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="booth-challenge-title" className="font-heading text-2xl font-black">
                  Quick check, one sec
                </h2>
                <p className="mt-2 text-base text-white/55">This keeps the room open for everybody.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  challengePromiseRef.current?.reject(new Error('Quick check closed.'));
                  challengePromiseRef.current = null;
                  setChallengeOpen(false);
                }}
                className="flex min-h-11 min-w-11 items-center justify-center rounded-full
                  text-white/55 hover:bg-white/10"
                aria-label="Close quick check"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div ref={challengeRef} className="mt-5 min-h-[65px]" />
            {challengeError && (
              <p role="alert" className="mt-3 text-base font-bold text-[#FFB19C]">
                {challengeError}
              </p>
            )}
          </div>
        </div>
      )}

      {undo && (
        <div className="fixed bottom-4 left-3 z-[70] flex min-h-12 items-center gap-3
          rounded-full border border-white/15 bg-[#111415] px-4 text-base font-bold shadow-2xl"
        >
          Line changed
          <button
            type="button"
            onClick={restoreOption}
            className="flex min-h-11 items-center gap-2 rounded-full px-3 font-black"
            style={{ color }}
          >
            <Undo2 className="h-4 w-4" /> Undo
          </button>
        </div>
      )}
    </div>
  );
};

export default PizzulMoves;
